import React, { ChangeEvent, DragEvent, useRef, useState } from 'react'
import { IPortDetailsStep2 } from '@dataTypes/clinic.type'
import { useAppSelector } from '@store/hooks'
import { Button, InputField } from '@enamelai/design-system'
import { Icon as UploadIcon } from '@enamelai/design-system/icons/Upload'

interface EnterDetailsProps {
  data: IPortDetailsStep2
  handleChange: (field: string, value: string | File) => void
  validationErrors: { [key: string]: string }
  existingDocumentLabel?: string | null
}

const EnterDetails: React.FC<EnterDetailsProps> = ({
  data,
  handleChange,
  validationErrors,
  existingDocumentLabel = null,
}) => {
  const MAX_UPLOAD_SIZE_BYTES = 4 * 1024 * 1024
  const clinicData = useAppSelector((state) => state.clinic.selectedClinic)
  const [uploadedFileName, setUploadedFileName] = useState('')
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isDragActive, setIsDragActive] = useState(false)
  const frontFileInputRef = useRef<HTMLInputElement>(null)

  const setSelectedFile = (file: File | null): void => {
    if (!file) return

    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      setUploadError('File size must be 4 MB or less.')
      setUploadedFileName('')
      if (frontFileInputRef.current) {
        frontFileInputRef.current.value = ''
      }
      return
    }

    setUploadError(null)
    handleChange('billing_statement_file', file)
    setUploadedFileName(file.name)
  }

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>): void => {
    setSelectedFile(event.target.files?.[0] || null)
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault()
    setIsDragActive(false)
    setSelectedFile(event.dataTransfer.files?.[0] || null)
  }

  return (
    <div className="scp-pm-card">
      <div className="scp-pm-grid2">
        <InputField
          variant="text"
          fullWidth
          label="Full name"
          value={String(data.full_name || '')}
          onChange={(event) => handleChange('full_name', event.target.value)}
          error={!!validationErrors.full_name}
          helperText={validationErrors.full_name}
          clearable={false}
        />
        <InputField
          variant="email"
          fullWidth
          label="Email address"
          value={String(data.email_address || '')}
          onChange={(event) =>
            handleChange('email_address', event.target.value)
          }
          error={!!validationErrors.email_address}
          helperText={validationErrors.email_address}
          clearable={false}
        />
      </div>
      <InputField
        variant="text"
        fullWidth
        label="Address"
        value={clinicData?.address || ''}
        disabled
      />
      <div className="scp-pm-grid2 scp-pm-grid-40">
        <InputField
          variant="text"
          fullWidth
          label="City"
          value={clinicData?.city || ''}
          disabled
          clearable={false}
        />
        <InputField
          variant="text"
          fullWidth
          label="State"
          value={clinicData?.state || ''}
          disabled
          clearable={false}
        />
        <InputField
          variant="text"
          fullWidth
          label="Zip"
          value={clinicData?.zip || ''}
          disabled
          clearable={false}
        />
      </div>
      <span className="scp-pm-blue-label">Wireless number (Optional)</span>
      <div className="scp-pm-grid2 scp-pm-grid-40">
        <InputField
          variant="phone"
          fullWidth
          label="Phone number"
          value={data.phone_number}
          onChange={(event) => handleChange('phone_number', event.target.value)}
          error={!!validationErrors.phone_number}
          helperText={validationErrors.phone_number}
          clearable={false}
        />
        <InputField
          variant="text"
          fullWidth
          label="Wireless account number"
          value={data.wireless_account_number}
          onChange={(event) =>
            handleChange('wireless_account_number', event.target.value)
          }
          error={!!validationErrors.wireless_account_number}
          helperText={validationErrors.wireless_account_number}
          clearable={false}
        />
        <InputField
          variant="text"
          fullWidth
          label="Pin or SSN"
          value={data.pin_or_ssn}
          onChange={(event) => handleChange('pin_or_ssn', event.target.value)}
          error={!!validationErrors.pin_or_ssn}
          helperText={validationErrors.pin_or_ssn}
          clearable={false}
        />
      </div>
      <div>
        <span className="scp-pm-label">Upload document</span>
        <div
          className={`scp-pm-upload-card${isDragActive ? ' scp-pm-upload-card-drag-active' : ''}${validationErrors.billing_statement_file || uploadError ? ' scp-pm-upload-card-destructive' : ''}`}
          onDragOver={(event) => {
            event.preventDefault()
            setIsDragActive(true)
          }}
          onDragLeave={() => setIsDragActive(false)}
          onDrop={handleDrop}
        >
          <UploadIcon className="scp-pm-upload-icon" aria-hidden="true" />
          <span className="scp-pm-upload-hint">
            Drop your billing statement file here, or{' '}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => frontFileInputRef.current?.click()}
            >
              click here
            </Button>
            to upload
          </span>
          <span className="scp-pm-upload-subhint">
            PDF file (4 MB max size)
            {existingDocumentLabel && !uploadedFileName
              ? '. Upload a new file to replace the current document, or leave unchanged.'
              : ''}
          </span>
          {existingDocumentLabel && !uploadedFileName ? (
            <span className="scp-pm-upload-file-name">
              Current document: {existingDocumentLabel}
            </span>
          ) : null}
          {uploadedFileName ? (
            <span className="scp-pm-upload-file-name">{uploadedFileName}</span>
          ) : null}
          {uploadError || validationErrors.billing_statement_file ? (
            <span className="hint-text">
              {uploadError || validationErrors.billing_statement_file}
            </span>
          ) : null}
          <input
            ref={frontFileInputRef}
            type="file"
            className="scp-pm-upload-input"
            accept=".jpg,.jpeg,.png,.pdf"
            onChange={handleFileSelect}
          />
        </div>
      </div>
    </div>
  )
}

export default EnterDetails
